import { type ReactNode, useState } from "react";
import { Loader2, Pencil } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  COUNTRIES,
  FOUNDER_ORIGIN_GROUPS,
  US_STATES,
  lookupCountry,
  lookupFounderOrigin,
  lookupUSState,
} from "@/lib/options";
import { ConfirmExclusionDialog } from "./ConfirmExclusionDialog";
import { SectorCombobox } from "./SectorCombobox";

const OTHER_VALUE = "__other__";

// ─── Shared view-mode button ─────────────────────────────────────────────

interface ViewButtonProps {
  display: ReactNode;
  readOnly?: boolean;
  onClick: () => void;
}

function ViewButton({ display, readOnly, onClick }: ViewButtonProps) {
  return (
    <button
      type="button"
      disabled={readOnly}
      onClick={onClick}
      className={cn(
        "group flex h-8 w-full min-w-0 items-center gap-2 rounded-md px-2 text-left",
        !readOnly && "hover:bg-slate-50",
        readOnly && "cursor-default",
      )}
    >
      <span className="min-w-0 flex-1 truncate">{display}</span>
      {!readOnly && (
        <Pencil className="h-3 w-3 shrink-0 text-slate-300 group-hover:text-slate-500" />
      )}
    </button>
  );
}

// ─── Founder origin (grouped select + exclusion confirm) ─────────────────

export interface FounderOriginFieldProps {
  value: string | null;
  readOnly?: boolean;
  onSave: (newValue: string | null) => Promise<void>;
}

export function FounderOriginField({
  value,
  readOnly,
  onSave,
}: FounderOriginFieldProps) {
  const [editing, setEditing] = useState(false);
  const [pendingValue, setPendingValue] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const current = lookupFounderOrigin(value);
  const display =
    current?.label ?? (
      <span className="text-slate-400">—</span>
    );

  async function trySave(newValue: string) {
    if (newValue === value) {
      setEditing(false);
      return;
    }
    if (newValue === "us_native" && value !== "us_native") {
      setPendingValue(newValue);
      return;
    }
    await commit(newValue);
  }

  async function commit(newValue: string) {
    setSaving(true);
    try {
      await onSave(newValue);
      setEditing(false);
      setPendingValue(null);
    } finally {
      setSaving(false);
    }
  }

  async function confirmAndCommit() {
    if (!pendingValue) return;
    setBusy(true);
    try {
      await onSave(pendingValue);
      setPendingValue(null);
      setEditing(false);
    } finally {
      setBusy(false);
    }
  }

  if (!editing) {
    return (
      <ViewButton
        display={display}
        readOnly={readOnly}
        onClick={() => setEditing(true)}
      />
    );
  }

  return (
    <>
      <Select
        defaultOpen
        value={value ?? ""}
        onValueChange={(v) => void trySave(v)}
        onOpenChange={(open) => {
          if (!open && !saving && !pendingValue) setEditing(false);
        }}
      >
        <SelectTrigger className="h-8" autoFocus>
          <SelectValue placeholder="Select origin…" />
        </SelectTrigger>
        <SelectContent className="max-h-[420px]">
          {FOUNDER_ORIGIN_GROUPS.map((group, gi) => (
            <SelectGroup key={group.label}>
              {gi > 0 && <SelectSeparator />}
              <SelectLabel
                className={cn(
                  "text-[10px] font-semibold uppercase tracking-wider",
                  group.tone === "warning"
                    ? "text-rose-600"
                    : "text-slate-400",
                )}
              >
                {group.label}
              </SelectLabel>
              {group.options.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  className={cn(
                    opt.excluded && "text-rose-700 focus:bg-rose-50",
                  )}
                >
                  <div className="flex w-full items-center justify-between gap-3">
                    <span>{opt.label}</span>
                    {opt.hint && (
                      <span
                        className={cn(
                          "text-[10px] tabular-nums",
                          opt.excluded ? "text-rose-500" : "text-slate-400",
                        )}
                      >
                        {opt.hint}
                      </span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
      <ConfirmExclusionDialog
        open={pendingValue !== null}
        onOpenChange={(o) => {
          if (!o) {
            setPendingValue(null);
            setEditing(false);
          }
        }}
        title="Activate exclusion filter?"
        description='Marking the founder as "US native" will exclude this company from scoring. Its score will drop to 0 and it won’t be contacted automatically. Continue?'
        confirmLabel="Activate filter"
        busy={busy}
        onConfirm={confirmAndCommit}
      />
    </>
  );
}

// ─── Country field (preset list + Other → free text) ─────────────────────

export interface CountryFieldProps {
  value: string | null;
  readOnly?: boolean;
  onSave: (newValue: string | null) => Promise<void>;
}

export function CountryField({ value, readOnly, onSave }: CountryFieldProps) {
  const [editing, setEditing] = useState(false);
  const [otherMode, setOtherMode] = useState(false);
  const [otherDraft, setOtherDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const known = lookupCountry(value);
  const display = known?.label ?? value ?? <span className="text-slate-400">—</span>;

  function startEdit() {
    setEditing(true);
    setOtherMode(false);
    setOtherDraft(value && !known ? value : "");
  }

  async function commit(next: string | null) {
    if (next === value) {
      setEditing(false);
      setOtherMode(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
      setOtherMode(false);
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <ViewButton
        display={display}
        readOnly={readOnly}
        onClick={startEdit}
      />
    );
  }

  if (otherMode) {
    return (
      <div className="flex items-center gap-2">
        <Input
          autoFocus
          value={otherDraft}
          placeholder="Country name or code"
          onChange={(e) => setOtherDraft(e.target.value)}
          onBlur={() => void commit(otherDraft.trim() || null)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void commit(otherDraft.trim() || null);
            }
            if (e.key === "Escape") {
              e.preventDefault();
              setEditing(false);
              setOtherMode(false);
            }
          }}
          disabled={saving}
          className="h-8"
        />
        {saving && (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-slate-400" />
        )}
      </div>
    );
  }

  return (
    <Select
      defaultOpen
      value={known ? value ?? "" : ""}
      onValueChange={(v) => {
        if (v === OTHER_VALUE) {
          setOtherMode(true);
        } else {
          void commit(v);
        }
      }}
      onOpenChange={(open) => {
        if (!open && !saving && !otherMode) setEditing(false);
      }}
    >
      <SelectTrigger className="h-8" autoFocus>
        <SelectValue placeholder="Select country…" />
      </SelectTrigger>
      <SelectContent>
        {COUNTRIES.map((c) => (
          <SelectItem key={c.value} value={c.value}>
            {c.label}
          </SelectItem>
        ))}
        <SelectSeparator />
        <SelectItem value={OTHER_VALUE}>Other…</SelectItem>
      </SelectContent>
    </Select>
  );
}

// ─── State field (US only, preset list + Other → free text) ──────────────

export interface StateFieldProps {
  value: string | null;
  countryValue: string | null;
  readOnly?: boolean;
  onSave: (newValue: string | null) => Promise<void>;
}

export function StateField({
  value,
  countryValue,
  readOnly,
  onSave,
}: StateFieldProps) {
  const [editing, setEditing] = useState(false);
  const [otherMode, setOtherMode] = useState(false);
  const [otherDraft, setOtherDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const isUS = countryValue === "US";
  const known = isUS ? lookupUSState(value) : null;
  const display = known
    ? known.label
    : value || <span className="text-slate-400">—</span>;

  if (!isUS) {
    // Plain text edit when country isn't US (states are largely a US concept here).
    // Fall back to the simple text behavior — minimal effort, reuses InlineEditField pattern inline.
    return (
      <ViewButton
        display={display}
        readOnly={readOnly}
        onClick={() => {}}
      />
    );
  }

  function startEdit() {
    setEditing(true);
    setOtherMode(false);
    setOtherDraft(value && !known ? value : "");
  }

  async function commit(next: string | null) {
    if (next === value) {
      setEditing(false);
      setOtherMode(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
      setOtherMode(false);
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <ViewButton
        display={display}
        readOnly={readOnly}
        onClick={startEdit}
      />
    );
  }

  if (otherMode) {
    return (
      <div className="flex items-center gap-2">
        <Input
          autoFocus
          value={otherDraft}
          placeholder="State"
          onChange={(e) => setOtherDraft(e.target.value)}
          onBlur={() => void commit(otherDraft.trim() || null)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void commit(otherDraft.trim() || null);
            }
            if (e.key === "Escape") {
              e.preventDefault();
              setEditing(false);
              setOtherMode(false);
            }
          }}
          disabled={saving}
          className="h-8"
        />
        {saving && (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-slate-400" />
        )}
      </div>
    );
  }

  return (
    <Select
      defaultOpen
      value={known ? value ?? "" : ""}
      onValueChange={(v) => {
        if (v === OTHER_VALUE) {
          setOtherMode(true);
        } else {
          void commit(v);
        }
      }}
      onOpenChange={(open) => {
        if (!open && !saving && !otherMode) setEditing(false);
      }}
    >
      <SelectTrigger className="h-8" autoFocus>
        <SelectValue placeholder="Select state…" />
      </SelectTrigger>
      <SelectContent className="max-h-[360px]">
        {US_STATES.map((s) => (
          <SelectItem key={s.value} value={s.value}>
            {s.label}
          </SelectItem>
        ))}
        <SelectSeparator />
        <SelectItem value={OTHER_VALUE}>Other…</SelectItem>
      </SelectContent>
    </Select>
  );
}

// ─── Sector field — wraps SectorCombobox in the inline-edit shell ────────

export interface SectorFieldProps {
  value: string | null;
  /** Project slug — drives the dynamic sector list. */
  slug: string;
  readOnly?: boolean;
  onSave: (newValue: string | null) => Promise<void>;
}

export function SectorField({ value, slug, readOnly, onSave }: SectorFieldProps) {
  const [saving, setSaving] = useState(false);

  const display =
    value && value.trim() !== ""
      ? value
      : <span className="text-slate-400">—</span>;

  if (readOnly) {
    return <ViewButton display={display} readOnly onClick={() => {}} />;
  }

  async function commit(next: string | null) {
    if (next === value) return;
    setSaving(true);
    try {
      await onSave(next);
    } finally {
      setSaving(false);
    }
  }

  return (
    <SectorCombobox
      value={value}
      onChange={(v) => void commit(v)}
      slug={slug}
      saving={saving}
      placeholder="—"
      triggerClassName="h-8 border-0 bg-transparent px-2 shadow-none hover:bg-slate-50"
      clearable
    />
  );
}
